import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ColorPicker } from "@/components/ui/color-picker";
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Plus, Users, UserMinus } from "lucide-react";
import { LogoLoader } from '@/components/ui/logo-loader';
import { useAlert } from '@/hooks/use-alert';
import type { ProjectTabProps } from '../types/project-details.types';
import { TicketSelectionCheckbox } from './TicketSelectionCheckbox';
import { TicketSelectAllCheckbox } from './TicketSelectAllCheckbox';
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import iconDeleteSvg from '@/assets/circle_delete.svg';

type GroupsTabProps = ProjectTabProps & {
  searchTerm: string;
  canManageGroups?: boolean;
  onCreateGroupReady?: (open: () => void) => void;
  onDeleteSelectedReady?: (removeSelected: () => void) => void;
  onSelectionChange?: (count: number) => void;
};

const getProjectTeamEntryId = (member: any) =>
  [
    member?.projectTeamEntryId,
    member?.teamEntryId,
    member?.id,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find(Boolean) || '';

export const GroupsTab: React.FC<GroupsTabProps> = ({
  projectId,
  searchTerm,
  canManageGroups = true,
  onCreateGroupReady,
  onDeleteSelectedReady,
  onSelectionChange,
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const { confirm } = useAlert();
  const queryClient = useQueryClient();
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [groupFormData, setGroupFormData] = useState({ name: '', description: '', color: '#3B82F6' });
  const [addingMemberToGroupId, setAddingMemberToGroupId] = useState<string | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  // Fetch team groups for this project
  const { data: teamGroupsResponse, isLoading: isLoadingGroups } = useQuery<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      description?: string;
      color?: string;
      createdAt: string;
    }>;
  }>({
    queryKey: ["/api/team-groups", projectId],
    queryFn: async () => {
      const response = await apiRequest(`/api/team-groups?projectId=${projectId}`);
      const data = await response.json();
      return data;
    },
    enabled: !!projectId
  });

  // Ensure teamGroups is always an array
  const teamGroups = Array.isArray(teamGroupsResponse?.data) 
    ? teamGroupsResponse.data 
    : Array.isArray(teamGroupsResponse) 
      ? teamGroupsResponse 
      : [];

  // Fetch all project members for adding to groups
  const { data: projectMembersData } = useQuery<Array<{
    id: string; 
    firstName?: string;
    lastName?: string;
    displayName?: string;
    position?: string;
    email?: string;
    // Backward compatibility
    name?: string;
  }>>({
    queryKey: ["/api/projects", projectId, "members"],
    queryFn: async () => {
      const response = await apiRequest(`/api/projects/${projectId}/members`);
      const data = await response.json();
      const members = data.data || [];
      return Array.isArray(members) ? members : [];
    },
    enabled: !!projectId
  });

  // Ensure allTeamMembers is always an array and map to expected format
  const allTeamMembers = (() => {
    const members = Array.isArray(projectMembersData) ? projectMembersData : [];
    
    // Map to expected format (name, lastName, etc.)
    return members.map((member: any) => ({
      id: member.id,
      teamEntryId: member.teamEntryId || member.id,
      linkedUserId: member.linkedUserId || member.userId || member.user_id || '',
      name: member.firstName || member.first_name || member.name || '',
      lastName: member.lastName || member.last_name || '',
      displayName: member.displayName || member.display_name || `${member.firstName || member.first_name || ''} ${member.lastName || member.last_name || ''}`.trim(),
      position: member.position || '',
      email: member.email || ''
    }));
  })();

  // Fetch group members for each group
  const { data: groupMembers = {} } = useQuery<Record<string, any[]>>({
    queryKey: ["/api/team-groups/members", projectId, teamGroups.map(g => g.id)],
    queryFn: async () => {
      const members: Record<string, any[]> = {};
      for (const group of teamGroups) {
        try {
          const response = await apiRequest(`/api/team-groups/${group.id}/members`);
          const data = await response.json();
          members[group.id] = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
          console.error(`Error fetching members for group ${group.id}:`, error);
          members[group.id] = [];
        }
      }
      return members;
    },
    enabled: teamGroups.length > 0
  });

  // Group management mutations
  const createGroupMutation = useMutation({
    mutationFn: async (groupData: any) => {
      const response = await apiRequest("/api/team-groups", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...groupData, projectId })
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-groups", projectId] });
      setShowGroupModal(false);
      setSelectedGroup(null);
      setGroupFormData({ name: '', description: '', color: '#3B82F6' });
    }
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest(`/api/team-groups/${id}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-groups", projectId] });
      setShowGroupModal(false);
      setSelectedGroup(null);
      setGroupFormData({ name: '', description: '', color: '#3B82F6' });
    }
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(`/api/team-groups/${id}`, {
        method: "DELETE"
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-groups", projectId] });
    }
  });

  const addMemberToGroupMutation = useMutation({
    mutationFn: async ({ groupId, projectTeamEntryId }: { groupId: string; projectTeamEntryId: string }) => {
      setAddingMemberToGroupId(groupId);
      const response = await apiRequest(`/api/team-groups/${groupId}/members`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectTeamEntryId })
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-groups/members", projectId] });
      setAddingMemberToGroupId(null);
    },
    onError: () => {
      setAddingMemberToGroupId(null);
    }
  });

  const removeMemberFromGroupMutation = useMutation({
    mutationFn: async ({ groupId, projectTeamEntryId }: { groupId: string; projectTeamEntryId: string }) => {
      const response = await apiRequest(`/api/team-groups/${groupId}/members/${projectTeamEntryId}`, {
        method: "DELETE"
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-groups/members", projectId] });
    }
  });

  const handleCreateGroup = useCallback(() => {
    if (!canManageGroups) return;
    setSelectedGroup(null);
    setGroupFormData({ name: '', description: '', color: '#3B82F6' });
    setShowGroupModal(true);
  }, [canManageGroups]);

  useEffect(() => {
    if (onCreateGroupReady) {
      onCreateGroupReady(canManageGroups ? handleCreateGroup : () => {});
    }
  }, [canManageGroups, handleCreateGroup, onCreateGroupReady]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedGroupIds.size);
    }
  }, [onSelectionChange, selectedGroupIds]);

  const handleEditGroup = (group: any) => {
    if (!canManageGroups) return;
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      color: group.color || '#3B82F6'
    });
    setShowGroupModal(true);
  };

  const handleSaveGroup = () => {
    if (!canManageGroups) return;
    if (selectedGroup) {
      updateGroupMutation.mutate({ id: selectedGroup.id, data: groupFormData });
    } else {
      createGroupMutation.mutate(groupFormData);
    }
  };

  const toggleGroupSelection = useCallback((groupId: string) => {
    if (!canManageGroups) return;
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, [canManageGroups]);

  const toggleAllGroups = useCallback((groupIds: string[]) => {
    if (!canManageGroups) return;
    setSelectedGroupIds((prev) => {
      const allSelected = groupIds.length > 0 && groupIds.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(groupIds);
    });
  }, [canManageGroups]);

  const deleteSelectedGroups = useCallback(async () => {
    if (!canManageGroups) return;
    if (selectedGroupIds.size === 0) return;
    const result = await confirm({
      message: t('confirmations.deleteGroup'),
      variant: 'destructive'
    });
    if (!result) return;

    setIsDeletingSelected(true);
    try {
      for (const groupId of selectedGroupIds) {
        await deleteGroupMutation.mutateAsync(groupId);
      }
      setSelectedGroupIds(new Set());
    } finally {
      setIsDeletingSelected(false);
    }
  }, [canManageGroups, confirm, deleteGroupMutation, selectedGroupIds, t]);

  useEffect(() => {
    if (onDeleteSelectedReady) {
      onDeleteSelectedReady(canManageGroups ? deleteSelectedGroups : () => {});
    }
  }, [canManageGroups, deleteSelectedGroups, onDeleteSelectedReady]);

  const handleAddToGroup = (groupId: string, memberId: string) => {
    if (!canManageGroups) return;
    addMemberToGroupMutation.mutate({ groupId, projectTeamEntryId: memberId });
  };

  const handleRemoveFromGroup = (groupId: string, memberId: string) => {
    if (!canManageGroups) return;
    removeMemberFromGroupMutation.mutate({ groupId, projectTeamEntryId: memberId });
  };

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return teamGroups;
    const searchLower = searchTerm.toLowerCase();
    return teamGroups.filter(group => (
      group.name?.toLowerCase().includes(searchLower) ||
      group.description?.toLowerCase().includes(searchLower)
    ));
  }, [searchTerm, teamGroups]);

  if (isLoadingGroups) {
    return (
      <div className="p-6 text-center text-slate-500">
        <p>Loading groups...</p>
      </div>
    );
  }

  const sortedGroups = filteredGroups;
  const allVisibleIds = sortedGroups.map((group) => group.id);

  const hasSelection = selectedGroupIds.size > 0;

  return (
    <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
      <div className="flex gap-4 max-h-full min-h-0">
        <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
          {sortedGroups.length > 0 ? (
          <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 max-h-full">
            <div className="overflow-auto min-h-0 pb-4">
              <table className="min-w-full">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-slate-200">
                    <th 
                      className="px-3 py-3 text-left w-12"
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <TicketSelectAllCheckbox
                        isAllSelected={allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedGroupIds.has(id))}
                        isIndeterminate={selectedGroupIds.size > 0 && !allVisibleIds.every((id) => selectedGroupIds.has(id))}
                        onToggleAll={() => toggleAllGroups(allVisibleIds)}
                        disabled={!canManageGroups || isDeletingSelected}
                      />
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[260px]"
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <span className="truncate">{t('team.groupName')}</span>
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[360px]"
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <span className="truncate">{t('team.groupDescription')}</span>
                    </th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGroups.map((group) => (
                    <tr
                      key={group.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${canManageGroups ? 'cursor-pointer' : 'cursor-default'}`}
                      onClick={() => handleEditGroup(group)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <TicketSelectionCheckbox
                          ticketId={group.id}
                          isSelected={selectedGroupIds.has(group.id)}
                          onToggle={toggleGroupSelection}
                            disabled={!canManageGroups || isDeletingSelected}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: group.color || '#3B82F6' }}
                          ></div>
                          <span className="text-sm text-black truncate">{group.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm text-black truncate block">
                          {group.description || ''}
                        </span>
                      </td>
                      <td className="px-3 py-3"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-sm">{t('team.noGroups')}</p>
            <p className="text-xs mt-1">{t('team.createFirstGroup')}</p>
            <button
              onClick={handleCreateGroup}
              className="button button--primary button--sm mt-4"
              disabled={!canManageGroups}
            >
              <Plus className="h-3 w-3" />
              {t('team.createGroup')}
            </button>
          </div>
        )}
        </div>

        {/* Right Sidebar Menu */}
        <RightSidebar>
          <SidebarButtonGroup label={t('common:delete')} hideLabel>
            <SidebarIconButton
              icon={iconDeleteSvg}
              alt="Delete"
              onClick={deleteSelectedGroups}
              disabled={!canManageGroups || !hasSelection}
              isLoading={isDeletingSelected}
              title={t('common:delete')}
            />
          </SidebarButtonGroup>
        </RightSidebar>
      </div>

      {/* Group Creation/Edit Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="max-w-md bg-[#DDDDDD] rounded-[24px]" hideClose>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-sm">
                {selectedGroup ? t('team.editGroup') : t('team.createGroup')}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <IconButtonCancel
                  onClick={() => {
                    setShowGroupModal(false);
                    setSelectedGroup(null);
                    setGroupFormData({ name: '', description: '', color: '#3B82F6' });
                  }}
                  size={30}
                  title={t('common:cancel')}
                />
                <IconButtonSave
                  onClick={handleSaveGroup}
                  disabled={!canManageGroups || !groupFormData.name.trim() || createGroupMutation.isPending || updateGroupMutation.isPending}
                  size={30}
                  title={selectedGroup ? t('common:save') : t('team.createGroup')}
                />
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            <CornerLabelField label={t('team.groupName')}>
              <Input
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                placeholder={t('team.groupNamePlaceholder')}
                className="corner-label-field-input"
                disabled={!canManageGroups}
              />
            </CornerLabelField>
            
            <CornerLabelField label={t('team.groupDescription')}>
              <Input
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                placeholder={t('team.groupDescriptionPlaceholder')}
                className="corner-label-field-input"
                disabled={!canManageGroups}
              />
            </CornerLabelField>
            
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">
                {t('team.groupColor')}
              </label>
              <div className="mt-1">
                <ColorPicker
                  value={groupFormData.color}
                  onChange={(color) => setGroupFormData({ ...groupFormData, color })}
                  disabled={!canManageGroups}
                />
                <Input
                  value={groupFormData.color}
                  onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                  placeholder="#3B82F6"
                  className="text-xs mt-2"
                  disabled={!canManageGroups}
                />
              </div>
            </div>

            {selectedGroup && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500">
                  {t('team.members')}: {groupMembers[selectedGroup.id]?.length || 0}
                </div>
                
                {/* Group Members */}
                <div className="space-y-1">
                  {(groupMembers[selectedGroup.id] || []).map(member => (
                    <div key={member.id} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">
                        {member.displayName || `${member.firstName || member.name || ''} ${member.lastName || ''}`.trim()}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromGroup(selectedGroup.id, getProjectTeamEntryId(member))}
                        className="text-red-500 hover:text-red-700 p-0 h-auto"
                        disabled={!canManageGroups}
                      >
                        <UserMinus className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Add Member Dropdown */}
                <div className="pt-2 border-t border-slate-100">
                  <div className="relative">
                    <select
                      className={`w-full text-xs border border-slate-200 rounded px-2 py-1 pr-8 ${
                        addingMemberToGroupId === selectedGroup.id ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onChange={(e) => {
                        if (e.target.value && !addingMemberToGroupId) {
                          handleAddToGroup(selectedGroup.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                      disabled={!canManageGroups || addingMemberToGroupId === selectedGroup.id}
                    >
                      <option value="">{t('team.addMemberToGroup')}</option>
                      {allTeamMembers
                        .filter(member => {
                          const memberId = getProjectTeamEntryId(member);
                          if (!memberId) {
                            return false;
                          }
                          return !(groupMembers[selectedGroup.id] || []).some(
                            (gm) => getProjectTeamEntryId(gm) === memberId,
                          );
                        })
                        .map(member => (
                          <option
                            key={getProjectTeamEntryId(member) || member.id}
                            value={getProjectTeamEntryId(member)}
                          >
                            {member.name} {member.lastName || ''}
                          </option>
                        ))}
                    </select>
                    {addingMemberToGroupId === selectedGroup.id && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <LogoLoader size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
};
